/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import { Stack, Label, Dropdown, Separator, ActionButton, IconButton } from "@fluentui/react";
import styles from "./FieldDefinitions.module.css";
import { HuxTextBox } from "@nuance/hux-components";
import { IFieldDefinitionPanelOptionsProps, IFieldDefinitionPanelOptionsState } from ".";
import { FieldDefinitionPanelOptionsCommands } from "./FieldDefinitionPanelOptionsViewModel";
import { IPanelFooterProps } from "./IPanelFooterProps";
import { panelFooterProps } from "./FieldDefinitionPanelStackTokens";
import FieldDefinitionPanel from "./FieldDefinitionPanel";

const fieldDefinitionPanelDropdownCommands = new FieldDefinitionPanelOptionsCommands();
const cmds = fieldDefinitionPanelDropdownCommands.Cmds;

// const stackTokens = { childrenGap: 10 };

const FieldDefinitionPanelOptions = (props: IFieldDefinitionPanelOptionsProps): JSX.Element => {
    const initialState: IFieldDefinitionPanelOptionsState = {
        common: { ...props.common },
        data: { ...props.data },
        errors: {},
        touched: {},
        isValid: !props.common.isNew,
        isDirty: false
    };

    const [state, dispatch] = React.useReducer(
        fieldDefinitionPanelDropdownCommands.Reducer,
        initialState
    );

    const onSaveFieldDefinition = () => {
        if (props.common.onSaveContent != undefined) {
            const saveData = fieldDefinitionPanelDropdownCommands.Save(state);
            props.common.onSaveContent(saveData);
        }
    };

    const footerProps: IPanelFooterProps = {
        onSave: onSaveFieldDefinition,
        onCancel: props.common.onClosePanel,
        isNew: state.common.isNew,
        isDirty: state.isDirty,
        isValid: state.isValid,
        panelFooterProps
    };

    return (
        <FieldDefinitionPanel
            title={props.common.panelTitle}
            isOpen={props.common.isOpen}
            onClosePanel={props.common.onClosePanel}
            footerProps={footerProps}
            settingName={state.data.settingName}
            onChangeSettingName={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            settingNameErrorMessage={state.touched.settingName ? state.errors.settingName : ""}
            onBlurSettingName={name => dispatch(cmds.onBlur(name))}
            settingNameReadOnly={!props.common.isNew}
            titleValue={state.data.title}
            onChangeTitle={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            titleErrorMessage={state.touched.title ? state.errors.title : ""}
            onBlurTitle={name => dispatch(cmds.onBlur(name))}
            tooltipText={state.data.tooltip}
            helperText={state.data.helper}
            onHelpTextChange={changeData => {
                dispatch(cmds.onHelpTextChange(changeData));
            }}
            accessLevel={state.data.accessLevels}
            onAccessLevelChange={accessLevel => dispatch(cmds.onAccessLevelChanged(accessLevel))}
            auditChanges={state.data.auditChanges}
            onAuditChangesChanged={(checked: boolean) => dispatch(cmds.onAuditChanged(checked))}
            controlType={state.data.definitionType}
        >
            <Stack horizontal tokens={{ childrenGap: 5 }}>
                <Dropdown
                    options={state.data.dropDownItems?.map((item, idx) => ({
                        key: item.key,
                        text: `Option ${idx + 1}`
                    }))}
                    defaultSelectedKey={state.data.defaultKey}
                    label={"Default"}
                    onChange={(_, selected) =>
                        dispatch(
                            cmds.onDropdownItemDefaultKeyChanged(
                                selected !== undefined ? selected.key.toString() : ""
                            )
                        )
                    }
                />
            </Stack>

            {state.data.dropDownItems?.map((opt, idx) => {
                const frozen: boolean | undefined = props.frozenControlGuids?.has(
                    opt.key.toString()
                );

                return (
                    <div key={idx}>
                        <Separator />
                        <Stack horizontal>
                            <Label className={styles.paddingDropdownOption}>Option {idx + 1}</Label>
                            {!frozen && (
                                <IconButton
                                    iconProps={{
                                        iconName: "StatusCircleErrorX"
                                    }}
                                    title="Clear"
                                    ariaLabel="Clear"
                                    onClick={() =>
                                        dispatch(cmds.onRemoveDropdownItem(opt.key?.toString()))
                                    }
                                />
                            )}
                        </Stack>

                        <HuxTextBox
                            className={styles.indent}
                            label="Text"
                            required
                            name={`OptionText_${opt.key}`}
                            value={opt.text}
                            maxLength={50}
                            onChange={(_, newValue) =>
                                dispatch(
                                    cmds.onDropdownItemTextChanged({
                                        key: opt.key,
                                        value: opt.value,
                                        text: newValue ?? ""
                                    })
                                )
                            }
                        />
                        <HuxTextBox
                            disabled={frozen}
                            className={styles.indent}
                            label="Value"
                            name={`OptionValue_${opt.key}`}
                            required
                            value={opt.value}
                            maxLength={50}
                            onChange={(_, newValue) =>
                                dispatch(
                                    cmds.onDropdownItemValueChanged({
                                        key: opt.key,
                                        value: newValue ?? "",
                                        text: opt.text
                                    })
                                )
                            }
                        />
                    </div>
                );
            })}

            <ActionButton
                iconProps={{ iconName: "Add" }}
                allowDisabledFocus
                onClick={() => dispatch(cmds.onAddDropdownItem())}
            >
                Add another option
            </ActionButton>
        </FieldDefinitionPanel>
    );
};

// Options are either dropdown or radio :)
const DropdownPanel = FieldDefinitionPanelOptions;
const RadioPanel = FieldDefinitionPanelOptions;
export { DropdownPanel, RadioPanel };
